import { useEffect, useState, type ReactNode } from 'react';

import { api, esquecerTokenInstalacao, guardarTokenInstalacao } from '../api';
import { Aviso, Campo, Painel } from '../comum';
import type { EstadoInstalacao, EtapaTeste, RespostaConfig } from '../tipos';

/**
 * Assistente de primeira execução.
 *
 * Existe porque o produto é **instalado no ambiente do cliente** por um técnico
 * que pode não ser desenvolvedor e pode não ter shell na máquina. Sem esta
 * tela, a instalação seria "edite o .env por SSH e reinicie" — que é onde a
 * venda morre.
 *
 * Duas decisões de desenho:
 *
 *  1. **Toda etapa que depende de outra máquina é testada antes de seguir.**
 *     Configuração validada só no primeiro uso real transforma instalação de
 *     uma hora em visita de retorno.
 *  2. **A escrita no KACE não é ligada aqui.** A instalação nasce em
 *     somente-leitura e o cliente libera quando confiar. Ligar tudo no minuto
 *     zero é convite a incidente.
 */

type Etapa = 'token' | 'kace' | 'ia' | 'automacao' | 'fim';

export function Assistente({
  estado,
  aoConcluir,
}: {
  estado: EstadoInstalacao;
  aoConcluir: () => void;
}): ReactNode {
  const [etapa, setEtapa] = useState<Etapa>(estado.exigeToken ? 'token' : 'kace');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [token, setToken] = useState('');
  const [kace, setKace] = useState({ baseUrl: '', organizacao: '', usuario: '', senha: '' });
  const [senhaJaGravada, setSenhaJaGravada] = useState(false);
  const [testeKace, setTesteKace] = useState<readonly EtapaTeste[] | null>(null);
  const [kaceOk, setKaceOk] = useState(false);

  const [ia, setIa] = useState({ modo: 'simulado', baseUrl: '', modelo: '', chave: '' });
  const [testeIa, setTesteIa] = useState<{ ok: boolean; detalhe: string } | null>(null);

  const [automacao, setAutomacao] = useState({ sync: true, sentinela: true });
  const [nomeDoCliente, setNomeDoCliente] = useState(estado.nomeDoCliente);

  /**
   * Carrega o que já está configurado, assim que o token é aceito.
   *
   * Uma instalação chega aqui com configuração parcial mais vezes do que
   * vazia: variável de ambiente entregue pelo orquestrador, reinstalação sobre
   * um volume existente, ou o assistente abandonado no meio e retomado no dia
   * seguinte. Obrigar o técnico a redigitar o que o produto já sabe é como se
   * perde a confiança dele na primeira tela — e é como uma senha correta vira
   * uma senha digitada errado.
   *
   * A senha nunca volta do servidor; o que volta é se existe uma.
   */
  useEffect(() => {
    if (etapa === 'token') return;
    let vivo = true;

    void api
      .get<RespostaConfig>('/api/config')
      .then((r) => {
        if (!vivo) return;
        const c = r.configuracao;
        setKace({
          baseUrl: c.kace.baseUrl,
          organizacao: c.kace.organizacao,
          usuario: c.kace.usuario,
          senha: '',
        });
        setSenhaJaGravada(c.kace.senhaDefinida);
        setIa({
          modo: c.ia.modo,
          baseUrl: c.ia.baseUrl ?? '',
          modelo: c.ia.modelo ?? '',
          chave: '',
        });
        setAutomacao({ sync: c.sync.automatico, sentinela: c.sentinela.automatico });
        if (c.instalacao.nomeDoCliente !== '') setNomeDoCliente(c.instalacao.nomeDoCliente);
      })
      .catch(() => {
        // Sem configuração legível, o assistente segue com os campos em branco —
        // que é o caminho de uma instalação realmente nova.
      });

    return () => {
      vivo = false;
    };
    // Só na entrada da etapa de conexão: recarregar a cada tecla apagaria o que
    // o técnico acabou de digitar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa === 'token']);

  async function proteger(f: () => Promise<void>): Promise<void> {
    setOcupado(true);
    setErro(null);
    try {
      await f();
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  const conferirToken = (): Promise<void> =>
    proteger(async () => {
      guardarTokenInstalacao(token.trim());
      try {
        await api.post('/api/config/instalacao/token', { token: token.trim() });
        setEtapa('kace');
      } catch (e: unknown) {
        // Token errado não pode ficar guardado: as requisições seguintes
        // mandariam um cabeçalho inválido e o erro viraria outro.
        esquecerTokenInstalacao();
        throw e;
      }
    });

  const testarKace = (): Promise<void> =>
    proteger(async () => {
      const r = await api.post<{ ok: boolean; etapas: readonly EtapaTeste[] }>(
        '/api/config/kace/testar',
        kace,
      );
      setTesteKace(r.etapas);
      setKaceOk(r.ok);
    });

  const salvarKace = (): Promise<void> =>
    proteger(async () => {
      await api.put<RespostaConfig>('/api/config/kace', kace);
      setEtapa('ia');
    });

  const testarIa = (): Promise<void> =>
    proteger(async () => {
      setTesteIa(await api.post<{ ok: boolean; detalhe: string }>('/api/config/ia/testar', ia));
    });

  const salvarIa = (): Promise<void> =>
    proteger(async () => {
      await api.put('/api/config/ia', ia);
      setEtapa('automacao');
    });

  const salvarAutomacao = (): Promise<void> =>
    proteger(async () => {
      await api.put('/api/config/sync', { automatico: automacao.sync, intervaloMin: 60 });
      await api.put('/api/config/sentinela', {
        automatico: automacao.sentinela,
        intervaloMin: 360,
      });
      setEtapa('fim');
    });

  const concluir = (): Promise<void> =>
    proteger(async () => {
      await api.post('/api/config/instalacao/concluir', { nomeDoCliente });
      esquecerTokenInstalacao();
      aoConcluir();
    });

  return (
    <div className="tela-cheia">
      <div className="caixa larga">
        <h1>Instalação da Plataforma Lenanzo</h1>
        <p className="suave" style={{ marginTop: 0 }}>
          Quatro etapas. Cada uma é testada antes da seguinte.
        </p>

        {erro !== null && <Aviso tipo="erro">{erro}</Aviso>}

        {etapa === 'token' && (
          <Painel
            titulo="1. Token de instalação"
            sub="O login é validado contra o KACE. Como a conexão ainda não existe, ninguém consegue entrar — por isso o serviço imprime um token no log ao subir."
          >
            <Campo
              rotulo="Token"
              ajuda="Procure por TOKEN DE INSTALAÇÃO na saída do serviço (docker compose logs, ou o terminal onde ele foi iniciado)."
            >
              <input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mono"
                autoFocus
              />
            </Campo>
            <button
              className="primario"
              disabled={ocupado || token.trim() === ''}
              onClick={() => void conferirToken()}
            >
              Continuar
            </button>
          </Painel>
        )}

        {etapa === 'kace' && (
          <Painel
            titulo="2. Conexão com o KACE"
            sub="Use uma conta de serviço com menor privilégio e SEM 2FA — a API do KACE rejeita autenticação de conta com segundo fator."
          >
            <div className="grade2">
              <Campo rotulo="Endereço do appliance" ajuda="Só o host, sem caminho.">
                <input
                  value={kace.baseUrl}
                  placeholder="https://kace.empresa.com.br"
                  onChange={(e) => setKace({ ...kace, baseUrl: e.target.value })}
                />
              </Campo>
              <Campo
                rotulo="Organização"
                ajuda='Exatamente como aparece no KACE, com o prefixo numérico (ex.: "1. Default").'
              >
                <input
                  value={kace.organizacao}
                  onChange={(e) => setKace({ ...kace, organizacao: e.target.value })}
                />
              </Campo>
              <Campo rotulo="Usuário">
                <input
                  value={kace.usuario}
                  onChange={(e) => setKace({ ...kace, usuario: e.target.value })}
                />
              </Campo>
              <Campo
                rotulo="Senha"
                ajuda={
                  senhaJaGravada
                    ? 'Já existe uma senha gravada. Deixe em branco para mantê-la.'
                    : undefined
                }
              >
                <input
                  type="password"
                  value={kace.senha}
                  placeholder={senhaJaGravada ? '••••••••' : ''}
                  onChange={(e) => setKace({ ...kace, senha: e.target.value })}
                />
              </Campo>
            </div>

            <div className="linha">
              <button
                className="encolhe"
                disabled={ocupado}
                onClick={() => void testarKace()}
              >
                {ocupado ? 'Testando…' : 'Testar conexão'}
              </button>
              <button
                className="primario encolhe"
                disabled={ocupado || !kaceOk}
                onClick={() => void salvarKace()}
              >
                Salvar e continuar
              </button>
              <span className="cresce" />
            </div>

            {testeKace !== null && <ResultadoTeste etapas={testeKace} />}
          </Painel>
        )}

        {etapa === 'ia' && (
          <Painel
            titulo="3. Inteligência artificial"
            sub="O modelo roda dentro da sua instalação. Nenhum dado sai do ambiente nos modos Simulado e Local."
          >
            <Campo rotulo="Modo">
              <select
                value={ia.modo}
                onChange={(e) => setIa({ ...ia, modo: e.target.value })}
              >
                <option value="simulado">Simulado — tela navegável, respostas de demonstração</option>
                <option value="local">Local — modelo dentro do seu ambiente</option>
                <option value="remoto">Remoto — envia dados para fora do ambiente</option>
                <option value="desligado">Desligado — recursos de IA ocultos</option>
              </select>
            </Campo>

            {ia.modo === 'remoto' && (
              <Aviso tipo="alerta">
                O modo remoto envia conteúdo de chamados e inventário para um serviço
                externo. Se a soberania de dados foi parte da decisão de compra, este
                não é o modo esperado.
              </Aviso>
            )}

            {(ia.modo === 'local' || ia.modo === 'remoto') && (
              <div className="grade2">
                <Campo rotulo="Endereço" ajuda="API compatível com OpenAI. Costuma terminar em /v1.">
                  <input
                    value={ia.baseUrl}
                    placeholder="http://localhost:11434/v1"
                    onChange={(e) => setIa({ ...ia, baseUrl: e.target.value })}
                  />
                </Campo>
                <Campo rotulo="Modelo">
                  <input
                    value={ia.modelo}
                    placeholder="qwen2.5:14b"
                    onChange={(e) => setIa({ ...ia, modelo: e.target.value })}
                  />
                </Campo>
                {ia.modo === 'remoto' && (
                  <Campo rotulo="Chave de API">
                    <input
                      type="password"
                      value={ia.chave}
                      onChange={(e) => setIa({ ...ia, chave: e.target.value })}
                    />
                  </Campo>
                )}
              </div>
            )}

            <div className="linha">
              <button className="encolhe" disabled={ocupado} onClick={() => void testarIa()}>
                Testar
              </button>
              <button
                className="primario encolhe"
                disabled={ocupado}
                onClick={() => void salvarIa()}
              >
                Salvar e continuar
              </button>
              <span className="cresce" />
            </div>

            {testeIa !== null && (
              <Aviso tipo={testeIa.ok ? 'ok' : 'erro'}>{testeIa.detalhe}</Aviso>
            )}
          </Painel>
        )}

        {etapa === 'automacao' && (
          <Painel
            titulo="4. Automação"
            sub="Pode ligar agora ou depois, em Configurações."
          >
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={automacao.sync}
                onChange={(e) => setAutomacao({ ...automacao, sync: e.target.checked })}
              />
              <span>
                <strong>Sincronizar automaticamente</strong>
                <span className="ajuda">
                  A cada hora, incremental. Sem isto os dados só atualizam no botão.
                </span>
              </span>
            </label>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={automacao.sentinela}
                onChange={(e) => setAutomacao({ ...automacao, sentinela: e.target.checked })}
              />
              <span>
                <strong>Varredura de conformidade automática</strong>
                <span className="ajuda">
                  A cada 6 horas, sobre o inventário já sincronizado. Não toca no
                  appliance e não executa nada — só detecta.
                </span>
              </span>
            </label>

            <Aviso>
              A escrita no KACE continua <strong>desligada</strong>. O produto vai
              observar e propor, sem alterar nada, até que um administrador libere em
              Configurações → Segurança.
            </Aviso>

            <button
              className="primario"
              disabled={ocupado}
              onClick={() => void salvarAutomacao()}
            >
              Salvar e continuar
            </button>
          </Painel>
        )}

        {etapa === 'fim' && (
          <Painel titulo="Concluir" sub="Depois disto o token de instalação deixa de valer e o acesso passa a ser pela credencial do KACE.">
            <Campo rotulo="Nome do cliente" ajuda="Aparece no cabeçalho e nos relatórios.">
              <input
                value={nomeDoCliente}
                onChange={(e) => setNomeDoCliente(e.target.value)}
              />
            </Campo>
            <button className="primario" disabled={ocupado} onClick={() => void concluir()}>
              Concluir instalação
            </button>
          </Painel>
        )}
      </div>
    </div>
  );
}

function ResultadoTeste({ etapas }: { etapas: readonly EtapaTeste[] }): ReactNode {
  const nomes: Record<string, string> = {
    dados: 'Dados informados',
    login: 'Autenticação',
    organizacao: 'Organização',
    inventario: 'Leitura de inventário',
    scripts: 'Acesso a scripts',
  };
  return (
    <ul className="passos">
      {etapas.map((e) => (
        <li key={e.etapa}>
          <span className="marca-passo">{e.ok ? '✓' : '✗'}</span>
          <span>
            <strong>{nomes[e.etapa] ?? e.etapa}</strong>
            <div className={e.ok ? 'suave' : ''} style={e.ok ? {} : { color: 'var(--erro)' }}>
              {e.detalhe}
            </div>
          </span>
        </li>
      ))}
    </ul>
  );
}
